import React from 'react';
import type { Traits } from '@/types/analysis';
import { parseTraits, getCategoryColor, getCategoryIcon } from '@/utils/traits';

interface BehavioralTraitsCardProps {
  traits: Traits | any; // Can be structured Traits or raw object from SSE
}

export const BehavioralTraitsCard: React.FC<BehavioralTraitsCardProps> = ({ traits }) => {
  console.log('🎯 BehavioralTraitsCard - Raw traits received:', traits);
  
  // Handle different trait data formats
  let parsedTraits;
  
  if (traits?.traits && Array.isArray(traits.traits)) {
    // Already in structured format (from older data structure or if pre-parsed)
    parsedTraits = traits.traits;
    console.log('🎯 BehavioralTraitsCard - Using pre-parsed array format');
  } else if (typeof traits === 'object' && traits !== null) {
    // Raw format from SSE - parse it
    parsedTraits = parseTraits(traits);
    console.log('🎯 BehavioralTraitsCard - Parsed traits:', parsedTraits);
  } else {
    parsedTraits = [];
    console.log('🎯 BehavioralTraitsCard - No valid traits data');
  }

  if (!parsedTraits || parsedTraits.length === 0) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">Behavioral Traits</h4>
          <span className="material-symbols-outlined text-2xl text-purple-500">
            psychology
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          No behavioral traits detected in this communication.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-medium text-gray-700">Behavioral Traits</h4>
        <span className="material-symbols-outlined text-2xl text-purple-500">
          psychology
        </span>
      </div>
      <div className="space-y-3 mt-3">
        {parsedTraits.map((trait: any, index: number) => {
          const displayName = trait.displayName || trait.trait || trait.name;
          const score = trait.score !== undefined ? trait.score : null;
          const category = trait.category || '';
          
          return (
            <div 
              key={index}
              className="flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-2 flex-1">
                <span className={`material-symbols-outlined text-lg ${getCategoryColor(category).split(' ')[0]}`}>
                  {getCategoryIcon(category)}
                </span>
                <span className="font-medium text-gray-800 text-sm">{displayName}</span>
              </div>
              
              <div className="flex items-center gap-2">
                {score !== null && (
                  <span className="text-xs text-gray-600 font-mono">
                    {Math.round(score * 100)}%
                  </span>
                )}
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${getCategoryColor(category)}`}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {traits?.summary && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-600">{traits.summary}</p>
        </div>
      )}
    </div>
  );
};

